import { Box3, Object3D, Vector3 } from 'three';

import { CesiumStore } from '../../../../stores/geodata-store-factory/cesium-point-cloud-store/cesium-point-cloud-store';
import { isDefined } from '../../../../utils/variables';
import { Cache } from '../../../cache/cache-manager';
import { SkyviewCamera } from '../../../cameras/types.js';
import { cullChunksToRender } from './cull-chunks-to-render';
import { queueChunksToLoad, TileNodeLoadResponse } from './queue-chunks-to-load';
import { TileNode, TileSet } from './types';
import {
  createTileNodeDataUri,
  extractTileNodeBoundingSphere,
  getNodeWithContent,
  isTileSetNode,
} from './utilities';

export class CesiumTileSetHierarchy {
  get rootTileSet() {
    return this._rootTileSet;
  }
  private _rootTileSet?: TileSet;

  constructor(private cesiumPointCloudStore: CesiumStore) {}

  async initialize() {
    this._rootTileSet = await this.cesiumPointCloudStore.fetchTileSet('tileset.json');
  }

  async filterMaxLodChunks(cache: Cache, offset: Vector3, bbox?: Box3): Promise<TileNode[]> {
    if (!isDefined(this._rootTileSet)) {
      throw new Error(
        'Root tileSet is not defined. Call initialize() before searching tile nodes.',
      );
    }

    const chunksToLoad: TileNode[] = [];

    const loadNode = async (tileNode: TileNode, index: number) => {
      // If it's not a tileset node, and it doesn't have child nodes then its max lod level.
      if (!isTileSetNode(tileNode) && (tileNode.children ?? []).length === 0) {
        chunksToLoad.push(tileNode);
        return;
      }

      // Search nodes within the bounding box recursively.
      for (const childNode of (tileNode.children ?? []).filter((x) =>
        isDefined(bbox) ? this.isTileNodeWithinBoundingBox(bbox, x, offset) : true,
      )) {
        let curChildNode = childNode;

        if (isTileSetNode(curChildNode)) {
          const childNodeUrl = createTileNodeDataUri(
            curChildNode,
            curChildNode.content?.uri as unknown as string,
          );
          if (!cache.isCached(childNodeUrl)) {
            cache.queue(childNodeUrl);
            const tileSet = await this.cesiumPointCloudStore.fetchTileSet(childNodeUrl);
            cache.addPointcloud(childNodeUrl, tileSet);
          }
          // If a child is cached and is a tileset, evaluate the tileset root node.
          const tileSetChild = cache.get(childNodeUrl) as TileSet;
          curChildNode = tileSetChild.root;
          await loadNode(tileSetChild.root, index + 1);
        } else {
          await loadNode(childNode, index + 1);
        }
      }
    };

    const nodeWithConent = getNodeWithContent(this._rootTileSet.root);
    if (isDefined(nodeWithConent)) {
      await loadNode(nodeWithConent, 0);
    }

    return chunksToLoad;
  }

  /**
   *  Returns which tile nodes are to be loaded based on camera frustum and distance
   */
  async filterTileNodesToBeLoaded(
    camera: SkyviewCamera,
    cache: Cache,
    offset: Vector3 = new Vector3(),
  ): Promise<TileNodeLoadResponse[]> {
    if (!isDefined(this._rootTileSet)) {
      throw new Error(
        'Root tileSet is not defined. Call initialize() before searching tile nodes.',
      );
    }

    // There is only a need to extract this once each iteration
    const view = {
      worldPosition: camera.getTHREECamera().getWorldPosition(new Vector3()),
      frustum: camera.getFrustum(),
      ray: camera.getRay(0, 0).ray,
      camera,
      offset,
    };

    const nodeWithContent = getNodeWithContent(this._rootTileSet.root);

    if (!isDefined(nodeWithContent)) {
      throw new Error('Empty tile set.');
    }

    const chunksToLoad: TileNodeLoadResponse[] = [];
    await queueChunksToLoad(
      this._rootTileSet.root,
      0,
      cache,
      view,
      this.cesiumPointCloudStore,
      chunksToLoad,
      nodeWithContent === this._rootTileSet.root,
    );

    // Always keep at least one node with content loaded.
    if (chunksToLoad.length === 0 && nodeWithContent.content?.uri) {
      const nodeWithContentUri = createTileNodeDataUri(
        nodeWithContent,
        nodeWithContent.content.uri,
      );
      if (!(cache.isCached(nodeWithContentUri) || cache.isQueued(nodeWithContentUri))) {
        chunksToLoad.push({ node: nodeWithContent, index: 0, distanceToRay: 0 });
      }
    }
    return chunksToLoad;
  }

  getNodeWithContent(node: TileNode): TileNode | undefined {
    if (isDefined(node.content?.uri)) {
      return node;
    }

    for (const child of node.children) {
      const nodeWithContent = getNodeWithContent(child);
      if (isDefined(nodeWithContent)) {
        return nodeWithContent;
      }
    }

    return undefined;
  }

  /**
   *  Returns which tile nodes are to be rendered based on camera frustum and distance
   *  Note: Split from above implementation to prepare for improved resolvment of chunks to render
   */
  resolveTileNodesToBeRendered(
    camera: SkyviewCamera,
    cache: Cache,
    offset: Vector3 = new Vector3(),
  ): Object3D[] | undefined {
    if (!isDefined(this._rootTileSet)) {
      throw new Error(
        'Root tileSet is not defined. Call initialize() before searching tile nodes.',
      );
    }

    // There is only a need to extract this once each iteration
    const view = {
      worldPosition: camera.getTHREECamera().getWorldPosition(new Vector3()),
      frustum: camera.getFrustum(),
      camera,
      offset,
    };

    const chunks = cullChunksToRender(this._rootTileSet.root, 0, cache, view)?.cachedChunks;
    return chunks;
  }

  private isTileNodeWithinBoundingBox(bbox: Box3, childNode: TileNode, offset: Vector3): boolean {
    const sphere = extractTileNodeBoundingSphere(childNode, offset);
    return bbox.intersectsSphere(sphere);
  }
}
